import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import plusIcon from '@/assets/svg/plus_icon.svg';
import { countWords } from '../utils/audioDescription';

interface WorkCoverPanelProps {
  currentLanguage: ContentLanguageCode;
  coverPreview: string | null;
  coverInputRef: React.RefObject<HTMLInputElement>;
  imageAlt: Record<ContentLanguageCode, string>;
  setImageAlt: (imageAlt: Record<ContentLanguageCode, string>) => void;
  markDirty: () => void;
  onCoverClick: () => void;
  onCoverSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCoverModify: (e: React.MouseEvent) => void;
  onCoverDelete: (e: React.MouseEvent) => void;
}

/**
 * Onglet « Vignette de l'épisode » (Figma admin 324:5701) : image commune aux
 * langues, description (texte alternatif) par langue, obligatoire pour publier.
 */
export const WorkCoverPanel: React.FC<WorkCoverPanelProps> = ({
  currentLanguage,
  coverPreview,
  coverInputRef,
  imageAlt,
  setImageAlt,
  markDirty,
  onCoverClick,
  onCoverSelect,
  onCoverModify,
  onCoverDelete,
}) => {
  const { t } = useTranslation();
  const altValue = imageAlt[currentLanguage] || '';
  const wordCount = countWords(altValue);

  return (
    <>
      <div className="create-work-section">
        <h4 className="section-label section-label--with-hint">{t('works.cover.fileLabel')}</h4>
        <p className="create-work-hint">{t('works.cover.fileHint')}</p>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          onChange={onCoverSelect}
          style={{ display: 'none' }}
          aria-label={t('works.cover.fileLabel')}
        />
        {coverPreview ? (
          <div className="upload-box has-image">
            <img src={coverPreview} alt="" className="upload-preview" />
            <div className="upload-overlay">
              <div className="image-action-buttons">
                <button type="button" className="image-action-btn modify-btn" onClick={onCoverModify}>
                  {t('common.edit')}
                </button>
                <button type="button" className="image-action-btn delete-btn" onClick={onCoverDelete}>
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button type="button" className="upload-box" onClick={onCoverClick}>
            <span className="plus-icon-container">
              <img src={plusIcon} alt="" className="plus-icon" />
            </span>
            <span>{t('exhibitions.addImage')}</span>
          </button>
        )}
      </div>

      {/* La description n'a de sens qu'avec une image (Figma : « apparaît une fois l'image chargée »). */}
      {coverPreview && (
        <div className="create-work-section">
          <label htmlFor="work-image-alt" className="section-label section-label--with-hint">
            {t('works.cover.altLabel')}
          </label>
          <p id="work-image-alt-hint" className="create-work-hint">
            {t('works.cover.altHint')}
          </p>
          <textarea
            id="work-image-alt"
            className="create-work-textarea"
            aria-describedby="work-image-alt-hint"
            rows={3}
            value={altValue}
            onChange={(e) => {
              markDirty();
              setImageAlt({ ...imageAlt, [currentLanguage]: e.target.value });
            }}
          />
          <p className="create-work-word-count" aria-live="polite">
            {t('exhibitions.audioDescription.wordCount', { count: wordCount })}
          </p>
        </div>
      )}
    </>
  );
};
